if (!customElements.get('product-gallery-modal')) {
  const TOUCH_QUERY = '(max-width: 1023px)';
  const MAX_SCALE = 6;
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_DISTANCE = 30;
  const TAP_SLOP = 10;
  const CLICK_GUARD_MS = 400;
  const HINT_VISIBLE_MS = 2500;
  const HINT_FADE_MS = 400;
  const HINT_STORAGE_KEY = 'product-gallery-zoom-hint-seen';
  const ANIMATED_CLASS = 'product-gallery-modal__media--animated';

  class ProductGalleryModal extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._sliderUnsub = null;
      this._sliderInitialized = false;
      this._activeIndex = null;
      this._zoom = { scale: 1, x: 0, y: 0 };
      this._zoomMedia = null;
      this._geometry = null;
      this._pinch = null;
      this._pan = null;
      this._tapStart = null;
      this._lastTap = null;
      this._ignoreClickUntil = 0;
      this._rafId = 0;
      this._hintShown = false;
      this._hintTimer = null;
      this._hintFadeTimer = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._dialog = this.querySelector('dialog');
      this._hint = this.querySelector('[data-zoom-hint]');
      this._hintInner = this._hint?.querySelector('.product-gallery-modal__hint-inner');
      this._zoomableMedias = [...this.querySelectorAll('.product-gallery-modal__media--zoom')];
      this._touchQuery = window.matchMedia(TOUCH_QUERY);

      this._zoomableMedias.forEach(media => {
        media.addEventListener('click', this._onMediaClick.bind(this), { signal });
        const wrapper = media.closest('.product-gallery-modal__media-wrapper');
        if (wrapper) this._bindTouchZoom(wrapper, media, signal);
      });

      this._touchQuery.addEventListener('change', this._onTouchModeChange.bind(this), { signal });
      this._dialog?.addEventListener('touchstart', () => this._hideHint(), { signal, capture: true, passive: true });

      const track = this.querySelector('.slider__list');
      if (track) {
        let dragOrigin = null;
        const startDrag = (point) => { dragOrigin = point; };
        const moveDrag = (point) => {
          if (!dragOrigin) return;
          if (Math.abs(point.x - dragOrigin.x) > 5 || Math.abs(point.y - dragOrigin.y) > 5) {
            dragOrigin = null;
            this._clearZoom();
          }
        };
        const endDrag = () => { dragOrigin = null; };

        track.addEventListener('mousedown', (e) => startDrag({ x: e.clientX, y: e.clientY }), { signal });
        track.addEventListener('mousemove', (e) => moveDrag({ x: e.clientX, y: e.clientY }), { signal });
        track.addEventListener('mouseup', endDrag, { signal });
        track.addEventListener('mouseleave', endDrag, { signal });

        track.addEventListener('touchstart', (e) => {
          const touch = e.touches[0];
          if (touch) startDrag({ x: touch.clientX, y: touch.clientY });
        }, { signal, passive: true });
        track.addEventListener('touchmove', (e) => {
          const touch = e.touches[0];
          if (touch) moveDrag({ x: touch.clientX, y: touch.clientY });
        }, { signal, passive: true });
        track.addEventListener('touchend', endDrag, { signal });
        track.addEventListener('touchcancel', endDrag, { signal });
      }

      this._dialog?.addEventListener('click', this._onDialogClick.bind(this), { signal });
      this._dialog?.addEventListener('click', this._onSliderNavClick.bind(this), { signal, capture: true });
      this._dialog?.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._dialog.close(); }, { signal });
      this._dialog?.addEventListener('open-to-slide', this._onOpenToSlide.bind(this), { signal });
      this._dialog?.addEventListener('close', this._onDialogClose.bind(this), { signal });

      this._sliderUnsub = theme.pubsub.subscribe(theme.pubsub.events.SLIDER_CHANGE, (data) => {
        const slider = this._dialog?.querySelector('theme-slider');
        if (!slider || data.id !== slider.id || data.index === this._activeIndex) return;
        this._activeIndex = data.index;
        this._clearZoom();
      });

      this._setupGalleryTriggers(signal);
    }

    _onSliderNavClick(event) {
      if (event.target.closest('.slider__button, .slider__counter__thumbnails .thumbnail')) {
        this._clearZoom();
      }
    }

    _setupGalleryTriggers(signal) {
      const sectionRoot = this.closest('[data-section-id]') || document;
      sectionRoot.addEventListener('click', this._onGalleryImageClick.bind(this), { signal });
    }

    _onGalleryImageClick(event) {
      const image = event.target.closest('.product-gallery-media__slide .product-media__image');
      if (!image) return;
      const slider = image.closest('theme-slider');
      if (slider?.classList.contains('moving')) return;

      const slide = image.closest('[data-media-id]');
      const mediaId = slide?.dataset.mediaId;

      this._dialog?.showModal();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._dialog?.dispatchEvent(new CustomEvent('open-to-slide', { detail: { mediaId } }));
        });
      });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._sliderUnsub?.();
      this._resetTouchZoom();
      this._hideHint(true);
    }

    _initSlider() {
      const slider = this._dialog?.querySelector('theme-slider');
      if (!slider) return;
      slider.setUpSlider?.();
      this._sliderInitialized = true;
    }

    _setMediaZoom() {
      this._zoomableMedias.forEach(media => {
        const mediaRatio = parseFloat(media.dataset.ratio) || 1;
        const wrapper = media.closest('.product-gallery-modal__media-wrapper');
        if (!wrapper) return;
        const wrapperRect = wrapper.getBoundingClientRect();
        const containerRatio = wrapperRect.width && wrapperRect.height
          ? wrapperRect.width / wrapperRect.height
          : window.innerWidth / window.innerHeight;
        media.classList.remove('vertical-zoom', 'horizontal-zoom');
        if (mediaRatio < containerRatio) {
          media.classList.add('vertical-zoom');
        } else if (mediaRatio > containerRatio) {
          media.classList.add('horizontal-zoom');
        }
      });
    }

    _onMediaClick(event) {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      const media = event.currentTarget;
      const wrapper = media.closest('.product-gallery-modal__media-wrapper');
      const slide = media.closest('.product-gallery-modal__slide');
      if (!wrapper || !slide) return;
      const slideRect = slide.getBoundingClientRect();
      const horizontalPosition = (event.clientX - slideRect.left) / slideRect.width;
      const verticalPosition = (event.clientY - slideRect.top) / slideRect.height;
      wrapper.classList.toggle('media-zoom');
      if (wrapper.classList.contains('media-zoom')) {
        requestAnimationFrame(() => {
          slide.scrollTo({
            left: (slide.scrollWidth - slideRect.width) * horizontalPosition,
            top: (slide.scrollHeight - slideRect.height) * verticalPosition
          });
        });
      }
    }

    _clearZoom() {
      this._resetTouchZoom();
      this._dialog?.querySelectorAll('.product-gallery-modal__media-wrapper.media-zoom').forEach(w => {
        w.classList.remove('media-zoom');
      });
      this._dialog?.querySelectorAll('.product-gallery-modal__slide').forEach(slide => {
        slide.scrollTo({ left: 0, top: 0 });
      });
    }

    _isTouchMode() {
      return this._touchQuery?.matches ?? false;
    }

    _onTouchModeChange() {
      this._clearZoom();
      this._hideHint(true);
    }

    _bindTouchZoom(wrapper, media, signal) {
      wrapper.addEventListener('touchstart', (e) => this._onTouchStart(e, wrapper, media), { signal, passive: true });
      wrapper.addEventListener('touchmove', (e) => this._onTouchMove(e, wrapper, media), { signal, passive: true });
      wrapper.addEventListener('touchend', (e) => this._onTouchEnd(e, wrapper, media), { signal, passive: false });
      wrapper.addEventListener('touchcancel', () => this._cancelGesture(), { signal, passive: true });
      ['gesturestart', 'gesturechange', 'gestureend'].forEach(type => {
        wrapper.addEventListener(type, (e) => { if (this._isTouchMode()) e.preventDefault(); }, { signal, passive: false });
      });
    }

    _wrapperTouches(touches, wrapper) {
      return [...touches].filter(touch => wrapper.contains(touch.target));
    }

    _measure(wrapper, media) {
      const rect = wrapper.getBoundingClientRect();
      return {
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        width: media.offsetWidth,
        height: media.offsetHeight,
        containerWidth: wrapper.clientWidth,
        containerHeight: wrapper.clientHeight,
        maxScale: MAX_SCALE
      };
    }

    _applyZoom(scale, x, y) {
      const geo = this._geometry;
      if (!geo) return;
      const clampedScale = Math.min(geo.maxScale, Math.max(1, scale));
      const limitX = Math.max(0, (geo.width * clampedScale - geo.containerWidth) / 2);
      const limitY = Math.max(0, (geo.height * clampedScale - geo.containerHeight) / 2);
      this._zoom = {
        scale: clampedScale,
        x: Math.min(limitX, Math.max(-limitX, x)),
        y: Math.min(limitY, Math.max(-limitY, y))
      };
      this._dialog?.classList.toggle('is-touch-zoomed', clampedScale > 1);
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = 0;
        this._renderZoom();
      });
    }

    _renderZoom() {
      if (!this._zoomMedia) return;
      const { scale, x, y } = this._zoom;
      if (scale > 1) this._zoomMedia.sizes = `${MAX_SCALE * 100}vw`;
      this._zoomMedia.style.transform = scale === 1 ? '' : `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }

    _resetTouchZoom() {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._rafId = 0;
      if (this._zoomMedia) {
        this._zoomMedia.style.transform = '';
        this._zoomMedia.classList.remove(ANIMATED_CLASS);
      }
      this._zoom = { scale: 1, x: 0, y: 0 };
      this._zoomMedia = null;
      this._geometry = null;
      this._pinch = null;
      this._pan = null;
      this._tapStart = null;
      this._lastTap = null;
      this._dialog?.classList.remove('is-touch-zoomed');
    }

    _cancelGesture() {
      this._pinch = null;
      this._pan = null;
      this._tapStart = null;
    }

    _beginGesture(wrapper, media) {
      if (this._zoomMedia && this._zoomMedia !== media) this._resetTouchZoom();
      this._zoomMedia = media;
      this._geometry = this._measure(wrapper, media);
      media.classList.remove(ANIMATED_CLASS);
    }

    _startPinch(touches) {
      const [a, b] = touches;
      const { centerX, centerY } = this._geometry;
      this._pinch = {
        distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1,
        scale: this._zoom.scale,
        x: this._zoom.x,
        y: this._zoom.y,
        midX: (a.clientX + b.clientX) / 2 - centerX,
        midY: (a.clientY + b.clientY) / 2 - centerY
      };
      this._pan = null;
      this._tapStart = null;
      this._lastTap = null;
    }

    _startPan(touch) {
      this._pan = { startX: touch.clientX, startY: touch.clientY, x: this._zoom.x, y: this._zoom.y, moved: false };
    }

    _onTouchStart(event, wrapper, media) {
      if (!this._isTouchMode()) return;
      const touches = this._wrapperTouches(event.touches, wrapper);
      if (!touches.length) return;
      this._beginGesture(wrapper, media);

      if (touches.length >= 2) {
        event.stopPropagation();
        this._startPinch(touches);
        return;
      }

      this._pinch = null;
      this._pan = null;
      this._tapStart = { x: touches[0].clientX, y: touches[0].clientY, time: performance.now() };
      if (this._zoom.scale > 1) {
        event.stopPropagation();
        this._startPan(touches[0]);
      }
    }

    _onTouchMove(event, wrapper, media) {
      if (!this._isTouchMode() || this._zoomMedia !== media) return;
      const touches = this._wrapperTouches(event.touches, wrapper);

      if (this._pinch && touches.length >= 2) {
        event.stopPropagation();
        const [a, b] = touches;
        const { centerX, centerY } = this._geometry;
        const pinch = this._pinch;
        const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const scale = Math.min(this._geometry.maxScale, Math.max(1, pinch.scale * distance / pinch.distance));
        const ratio = scale / pinch.scale;
        const midX = (a.clientX + b.clientX) / 2 - centerX;
        const midY = (a.clientY + b.clientY) / 2 - centerY;
        this._applyZoom(scale, midX - ratio * (pinch.midX - pinch.x), midY - ratio * (pinch.midY - pinch.y));
        return;
      }

      if (this._pan && touches.length === 1) {
        event.stopPropagation();
        const dx = touches[0].clientX - this._pan.startX;
        const dy = touches[0].clientY - this._pan.startY;
        if (Math.hypot(dx, dy) > TAP_SLOP) this._pan.moved = true;
        this._applyZoom(this._zoom.scale, this._pan.x + dx, this._pan.y + dy);
      }
    }

    _onTouchEnd(event, wrapper, media) {
      if (!this._isTouchMode() || this._zoomMedia !== media) return;
      const remaining = this._wrapperTouches(event.touches, wrapper);

      if (this._pinch) {
        if (remaining.length >= 2) return;
        this._pinch = null;
        this._ignoreClickUntil = performance.now() + CLICK_GUARD_MS;
        if (event.cancelable) event.preventDefault();
        if (this._zoom.scale <= 1) this._applyZoom(1, 0, 0);
        else if (remaining.length === 1) this._startPan(remaining[0]);
        return;
      }

      const wasPan = this._pan;
      this._pan = null;
      const tap = this._tapStart;
      this._tapStart = null;
      const touch = event.changedTouches[0];
      if (!tap || !touch || remaining.length) return;

      const now = performance.now();
      const moved = Math.hypot(touch.clientX - tap.x, touch.clientY - tap.y) > TAP_SLOP;
      if (moved || now - tap.time > DOUBLE_TAP_MS) {
        this._lastTap = null;
        if (wasPan?.moved) {
          this._ignoreClickUntil = now + CLICK_GUARD_MS;
          if (event.cancelable) event.preventDefault();
        }
        return;
      }

      const last = this._lastTap;
      if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(touch.clientX - last.x, touch.clientY - last.y) < DOUBLE_TAP_DISTANCE) {
        this._lastTap = null;
        this._ignoreClickUntil = now + CLICK_GUARD_MS;
        if (event.cancelable) event.preventDefault();
        this._toggleZoom(media, touch.clientX, touch.clientY);
        return;
      }
      this._lastTap = { x: touch.clientX, y: touch.clientY, time: now };
    }

    _toggleZoom(media, pointX, pointY) {
      media.classList.add(ANIMATED_CLASS);
      if (this._zoom.scale > 1) {
        this._applyZoom(1, 0, 0);
        return;
      }
      const geo = this._geometry;
      const scale = Math.min(DOUBLE_TAP_SCALE, geo.maxScale);
      this._applyZoom(scale, (1 - scale) * (pointX - geo.centerX), (1 - scale) * (pointY - geo.centerY));
    }

    _maybeShowHint(mediaId) {
      if (!this._hint || !this._hintInner || this._hintShown || !this._isTouchMode()) return;
      const slide = mediaId
        ? this._dialog?.querySelector(`.product-gallery-modal__slide[data-media-id="${mediaId}"]`)
        : this._dialog?.querySelector('.product-gallery-modal__slide');
      if (!slide?.querySelector('.product-gallery-modal__media--zoom')) return;

      this._hintShown = true;
      try {
        if (sessionStorage.getItem(HINT_STORAGE_KEY)) return;
        sessionStorage.setItem(HINT_STORAGE_KEY, 'true');
      } catch (e) {
        void e;
      }

      clearTimeout(this._hintFadeTimer);
      this._hintInner.hidden = false;
      requestAnimationFrame(() => this._hint.classList.add('is-visible'));
      this._hintTimer = setTimeout(() => this._hideHint(), HINT_VISIBLE_MS);
    }

    _hideHint(immediate = false) {
      if (!this._hint || !this._hintInner) return;
      clearTimeout(this._hintTimer);
      this._hintTimer = null;
      if (this._hintInner.hidden) return;
      this._hint.classList.remove('is-visible');
      clearTimeout(this._hintFadeTimer);
      if (immediate) {
        this._hintInner.hidden = true;
        return;
      }
      this._hintFadeTimer = setTimeout(() => { this._hintInner.hidden = true; }, HINT_FADE_MS);
    }

    _onDialogClick(event) {
      if (event.target === this._dialog) {
        this._dialog.close();
        return;
      }
      if (performance.now() < this._ignoreClickUntil || this._zoom.scale > 1) return;
      const slide = event.target.closest('.product-gallery-modal__slide');
      if (slide && !event.target.closest('.product-gallery-modal__media--zoom, .product-gallery-modal__video, .product-gallery-modal__iframe, .product-gallery-modal__model, .video-facade, .slider__button, .product-gallery-modal__close')) {
        this._dialog.close();
      }
    }

    _onOpenToSlide(event) {
      requestAnimationFrame(() => {
        if (!this._sliderInitialized) this._initSlider();

        this._clearZoom();
        this._setMediaZoom();

        const mediaId = event.detail?.mediaId;
        this._maybeShowHint(mediaId);
        this._activeIndex = 0;
        if (!mediaId) return;
        const slider = this._dialog?.querySelector('theme-slider');
        if (!slider) return;
        const slide = this._dialog?.querySelector(`.product-gallery-modal__slide[data-media-id="${mediaId}"]`);
        if (!slide) return;
        const index = parseInt(slide.dataset.index, 10);
        if (Number.isNaN(index)) return;
        this._activeIndex = index;
        this._navigateOnceLaidOut(slider, index);
      });
    }

    _navigateOnceLaidOut(slider, index, attempt = 0) {
      // A rAF-count fix was unreliable: the slide sometimes ended up thousands
      // of pixels off-canvas because the slider hadn't laid out yet. Poll for
      // real width instead, capped so a broken slider can't loop forever.
      if (slider.offsetWidth > 0 || attempt >= 20) {
        slider.reanchor?.();
        slider.navigateToSlide(index, false);
        return;
      }
      requestAnimationFrame(() => this._navigateOnceLaidOut(slider, index, attempt + 1));
    }

    _onDialogClose() {
      this._sliderInitialized = false;
      this._activeIndex = null;
      this._clearZoom();
      this._hideHint(true);
    }
  }

  customElements.define('product-gallery-modal', ProductGalleryModal);
}
